/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';
import moment from 'moment-timezone';
import { createSelector } from 'reselect';

import { ApiStatus } from '../../../../ts';
import { Release } from '../../../../ts/release';
import { ContentChange, ContentChangeType } from '../../../../ts/skill';
import { SnapshotCounts } from '../../../../ts/snapshot';
import type { RootState } from '../../../../ts/state';
import {
  getAllActions,
  selectLastModifiedAction
} from '../../../actions/store/actions/selectors';
import { selectAllCollections } from '../../../actions/store/collections/selectors';
import {
  selectAllEntities,
  selectAreEntitiesLoading
} from '../../../actions/store/entities/selectors';
import {
  areIntentsLoading,
  selectAllIntents
} from '../../../actions/store/intents/selectors';
import {
  getGlobalVariables,
  selectLastModifiedVariableTime
} from '../../../actions/store/variables/selectors';
import { isSyntheticAction } from '../../../actions/util/variableUtil';
import { getNodeMap } from '../../../dialog/store/dialog/selectors';
import {
  isAssistantReadOnly,
  isSkillReadOnly
} from '../access-control/selectors';
import {
  selectActionSkillId,
  selectDialogSkillId
} from '../assistants/core-selectors';
import {
  selectDraftEnvironment,
  selectIsDialogEnabledInDraftEnvironment,
  selectIsDialogEnabledInTargetEnvironment
} from '../environments/selectors';
import { selectLimits } from '../instance/selectors';
import { selectAllReleases } from '../releases/core-selectors';
import { getAssistantId } from '../session/selectors';
import { selectSkillById } from '../skills/core-selectors';
import {
  selectActionSnapshots,
  selectAreSkillSnapshotsLoading,
  selectDialogSnapshots
} from '../snapshots/selectors';

type Versions = {
  currentVersionId: string;
  currentVersion: Release;
  latestVersionId: string;
  latestVersion: Release;
  versions: Release[];
};
/**
 * Selects the versions to show in the UI. They come from the releases API.
 */
export const selectVersions = createSelector(
  [selectAllReleases],
  (allReleases: Release[]): Versions => {
    const sortedReleases = _.sortBy(allReleases, 'updated').reverse();

    const currentRelease = sortedReleases.find(r => r.is_live);
    const releases = currentRelease
      ? [
          currentRelease,
          ...sortedReleases.filter(r => r.release !== currentRelease.release)
        ]
      : sortedReleases;

    const latestRelease = _.maxBy(allReleases, 'created');

    return {
      latestVersion: latestRelease,
      latestVersionId: latestRelease?.release,
      currentVersionId: currentRelease?.release,
      currentVersion: currentRelease,
      versions: releases
    };
  }
);

export const selectVersionCount = createSelector(
  [selectAllReleases],
  (releases): number => releases?.length || 0
);

export const hasPublishedVersion = createSelector(
  [selectVersionCount],
  (count): boolean => count > 0
);

export const selectActionCounts = createSelector(
  [selectActionSkillId, selectVersions, selectActionSnapshots],
  (actionSkillId, versions, snapshots): SnapshotCounts => {
    const { latestVersion } = versions;

    const currentSnapshot = latestVersion?.content?.skills.find(
      s => s.skill_id === actionSkillId
    );

    return snapshots?.find(s => s?.snapshot === currentSnapshot?.snapshot)
      ?.counts;
  }
);

export const selectDialogCounts = createSelector(
  [selectDialogSkillId, selectVersions, selectDialogSnapshots],
  (dialogSkillId, versions, snapshots): SnapshotCounts => {
    const { latestVersion } = versions;

    const currentSnapshot = latestVersion?.content?.skills.find(
      s => s.skill_id === dialogSkillId
    );

    return snapshots?.find(s => s?.snapshot === currentSnapshot?.snapshot)
      ?.counts;
  }
);

export const selectActionChanges = createSelector(
  [getAllActions, selectVersions, selectActionCounts],
  (actions, versions, counts): ContentChange[] => {
    const { latestVersion } = versions;
    const isEmpty = !latestVersion;
    const lastPublished = latestVersion?.created;
    const actionsPublished = counts?.actions || 0;

    // Check existing actions to see if they were updated after the last publish
    const changes: ContentChange[] = actions
      .filter(action => !isSyntheticAction(action))
      .map(action => {
        const lastUpdated = action.updated;
        // based on last published i.e, when skill was last updated
        const isDirty = isEmpty || moment(lastPublished).isBefore(lastUpdated);
        return {
          name: action?.title,
          lastPublished,
          lastUpdated,
          isNew: moment(action.created).isAfter(lastPublished),
          isDirty,
          type: ContentChangeType.ACTIONS
        };
      });

    // If it's the first publish we return the new actions
    if (!latestVersion) {
      return changes;
    }

    // If there is an existing version live. Compare artifacts to find deleted
    // actions by looking at the number of updated actions in draft vs live.
    // If the number is different, then some draft actions were deleted
    const updatedActions = changes.filter(a => !a.isNew).length;
    const deletedActions = actionsPublished - updatedActions;

    if (deletedActions > 0) {
      changes.push({
        name: `${deletedActions} deleted actions`, // I can't use intl here :(
        lastPublished,
        lastUpdated: lastPublished,
        isNew: false,
        count: deletedActions,
        isDelete: true,
        isDirty: true,
        type: ContentChangeType.ACTIONS
      });
    }

    return changes;
  }
);

export const selectVariableChanges = createSelector(
  [getGlobalVariables, selectVersions, selectActionCounts],
  (variables, versions, counts): ContentChange[] => {
    const { latestVersion } = versions;
    const isEmpty = !latestVersion;
    const lastPublished = latestVersion?.created;
    const variablesPublished = counts?.global_variables || 0;

    const changes: ContentChange[] = variables.map(variable => {
      const lastUpdated = variable?.updated;
      const isDirty = isEmpty || moment(lastPublished).isBefore(lastUpdated);
      return {
        name: variable?.title,
        lastPublished,
        count: 0,
        isDelete: false,
        isNew: moment(variable.created).isAfter(lastPublished),
        lastUpdated,
        isDirty,
        type: ContentChangeType.VARIABLES
      };
    });

    // If it's the first publish we return the new variables
    if (!latestVersion) {
      return changes;
    }

    // If there is an existing version live. Compare artifacts to find deleted
    // variables by looking at the number of updated variables in draft vs live.
    // If the number is different, then some draft variables were deleted
    const updatedVariables = changes.filter(a => !a.isNew).length;
    const deletedVariables = variablesPublished - updatedVariables;

    if (deletedVariables > 0) {
      changes.push({
        name: `${deletedVariables} deleted variables`, // I can't use intl here :(
        lastPublished,
        lastUpdated: lastPublished,
        count: deletedVariables,
        isDelete: true,
        isNew: false,
        isDirty: true,
        type: ContentChangeType.VARIABLES
      });
    }

    return changes;
  }
);

export const selectActionSkillSettingsChanges = (
  state: RootState
): ContentChange[] => {
  const { latestVersion } = selectVersions(state);
  const skillId = selectActionSkillId(state);
  const skill = selectSkillById(state, skillId);
  const lastModifiedVariableTime = selectLastModifiedVariableTime(state);
  const lastModifiedAction = selectLastModifiedAction(state);

  const isEmpty = !latestVersion;
  const lastUpdated = skill?.updated;
  const lastPublished = latestVersion?.created;

  // Store updates the skill.updated when an action or variable is updated.
  // Check if the skill.update is equal to action.updated or variable.updated
  // and ignore skill changes if that's the case
  const isTransactionUpdate =
    lastUpdated === lastModifiedAction ||
    lastUpdated === lastModifiedVariableTime;

  const isDirty =
    isEmpty ||
    (!isTransactionUpdate && moment(lastPublished).isBefore(lastUpdated));

  return [
    {
      name: ContentChangeType.ACTIONS_SETTINGS,
      lastPublished,
      lastUpdated,
      isNew: false,
      isDirty,
      type: ContentChangeType.SETTINGS
    }
  ];
};

export const selectDialogSkillSettingsChanges = (
  state: RootState
): ContentChange[] => {
  const { latestVersion } = selectVersions(state);
  const skillId = selectDialogSkillId(state);
  const isEmpty = !latestVersion;

  const skill = selectSkillById(state, skillId);

  const lastUpdated = skill?.updated;
  const lastPublished = latestVersion?.created;

  const lastModifiedVariableTime = selectLastModifiedVariableTime(state);
  const lastModifiedAction = selectLastModifiedAction(state);

  // Store updates the skill.updated when an action or variable is updated.
  // Check if the skill.update is equal to action.updated or variable.updated
  // and ignore skill changes if that's the case
  const isTransactionUpdate =
    lastUpdated === lastModifiedAction ||
    lastUpdated === lastModifiedVariableTime;

  const isDirty =
    isEmpty ||
    (!isTransactionUpdate && moment(lastPublished).isBefore(lastUpdated));

  return [
    {
      name: ContentChangeType.DIALOG_SETTINGS,
      lastPublished,
      lastUpdated,
      isNew: false,
      isDirty,
      type: ContentChangeType.SETTINGS
    }
  ];
};

export const selectIntentChanges = createSelector(
  [
    state => state.intents?.props?.loading,
    selectAllIntents,
    selectVersions,
    selectDialogCounts
  ],
  (isLoading: boolean, intents, versions, counts): ContentChange[] => {
    const { latestVersion } = versions;
    const isEmpty = !latestVersion;
    const lastPublished = latestVersion?.created;
    const intentsPublished = counts?.intents || 0;

    if (isLoading && intents?.length === 0) {
      return [];
    }

    const changes: ContentChange[] = intents?.map(intent => {
      const lastUpdated = intent?.updated;
      const isDirty = isEmpty || moment(lastPublished).isBefore(lastUpdated);
      return {
        name: intent?.intent,
        count: 0,
        lastPublished,
        isNew: moment(intent.created).isAfter(lastPublished),
        isDelete: false,
        lastUpdated,
        isDirty,
        type: ContentChangeType.INTENTS
      };
    });

    // If it's the first publish we return the new variables
    if (!latestVersion) {
      return changes;
    }

    // If there is an existing version live. Compare artifacts to find deleted
    // intents by looking at the number of updated intents in draft vs live.
    // If the number is different, then some draft intents were deleted
    const updatedIntents = changes.filter(a => !a.isNew).length;
    const deletedIntents = intentsPublished - updatedIntents;

    if (deletedIntents > 0) {
      changes.push({
        name: `${deletedIntents} deleted intents`, // I can't use intl here :(
        lastPublished,
        lastUpdated: lastPublished,
        count: deletedIntents,
        isDelete: true,
        isNew: false,
        isDirty: true,
        type: ContentChangeType.INTENTS
      });
    }

    return changes;
  }
);

export const selectEntitiesChanges = createSelector(
  [
    state => state.entities?.props?.loading,
    selectAllEntities,
    selectVersions,
    selectDialogCounts
  ],
  (isLoading: boolean, entities, versions, counts): ContentChange[] => {
    const { latestVersion } = versions;

    const isEmpty = !latestVersion;
    const lastPublished = latestVersion?.created;
    const entitiesPublished = counts?.entities || 0;

    if (isLoading && entities?.length === 0) {
      return [];
    }

    const changes: ContentChange[] = entities
      ?.filter(entity => !entity.entity.startsWith('sys-synthetic_'))
      ?.map(entity => {
        const lastUpdated = entity?.updated;
        const isDirty = isEmpty || moment(lastPublished).isBefore(lastUpdated);
        return {
          name: entity?.entity,
          lastPublished,
          count: 0,
          // system entities have the same timestamp for both created and deleted,
          // source field helps up keep the count so that deleted entities are detected
          isNew: entity.source
            ? false
            : moment(entity?.created).isAfter(lastPublished),
          isDelete: false,
          lastUpdated,
          isDirty,
          type: ContentChangeType.ENTITIES
        };
      });

    // If it's the first publish we return the new variables
    if (!latestVersion) {
      return changes;
    }

    // If there is an existing version live. Compare artifacts to find deleted
    // entities by looking at the number of updated entities in draft vs live.
    // If the number is different, then some draft entities were deleted
    const updatedEntities = changes.filter(a => !a.isNew).length;
    const deletedEntities = entitiesPublished - updatedEntities;

    if (deletedEntities > 0) {
      changes.push({
        name: `${deletedEntities} deleted entities`, // I can't use intl here :(
        lastPublished,
        lastUpdated: lastPublished,
        count: deletedEntities,
        isDelete: true,
        isNew: false,
        isDirty: true,
        type: ContentChangeType.ENTITIES
      });
    }

    return changes;
  }
);

export const selectDialogChanges = createSelector(
  [
    (state: RootState) => state.dialog.loading,
    getNodeMap,
    selectVersions,
    selectDialogCounts
  ],
  (isLoading, nodes, versions, counts): ContentChange[] => {
    const { latestVersion } = versions;

    const isEmpty = !latestVersion;
    const lastPublished = latestVersion?.created;
    const dialogsPublished = counts?.dialog_nodes || 0;

    if (isLoading && nodes?.length === 0) {
      return [];
    }

    const changes: ContentChange[] = Object.keys(nodes).map(key => {
      const lastUpdated = nodes[key]?.updated;
      const isDirty = isEmpty || moment(lastPublished).isBefore(lastUpdated);
      return {
        name: nodes[key]?.name,
        lastPublished,
        count: 0,
        isDelete: false,
        isNew: moment(nodes[key].created).isAfter(lastPublished),
        lastUpdated,
        isDirty,
        type: ContentChangeType.DIALOGS
      };
    });

    // If it's the first publish we return the new variables
    if (!latestVersion) {
      return changes;
    }

    // If there is an existing version live. Compare artifacts to find deleted
    // entities by looking at the number of updated entities in draft vs live.
    // If the number is different, then some draft entities were deleted
    const updatedDialogs = changes.filter(a => !a.isNew).length;
    const deletedDialogNodes = dialogsPublished - updatedDialogs;

    if (deletedDialogNodes > 0) {
      changes.push({
        name: `${deletedDialogNodes} deleted dialog node`, // I can't use intl here :(
        lastPublished,
        lastUpdated: lastPublished,
        count: deletedDialogNodes,
        isDelete: true,
        isNew: false,
        isDirty: true,
        type: ContentChangeType.DIALOGS
      });
    }

    return changes;
  }
);

export const selectCollectionChanges = createSelector(
  [selectAllCollections, selectVersions, selectActionCounts],
  (collections, versions, counts): ContentChange[] => {
    const { latestVersion } = versions;
    const isEmpty = !latestVersion;
    const lastPublished = latestVersion?.created;
    const collectionsPublished = counts?.collections || 0;

    const changes: ContentChange[] = collections.map(collection => {
      const lastUpdated = collection?.updated;
      const isDirty = isEmpty || moment(lastPublished).isBefore(lastUpdated);
      return {
        name: collection?.title,
        lastPublished,
        count: 0,
        isDelete: false,
        isNew: moment(collection.created).isAfter(lastPublished),
        lastUpdated,
        isDirty,
        type: ContentChangeType.COLLECTIONS
      };
    });

    // If it's the first publish we return the new collections
    if (!latestVersion) {
      return changes;
    }

    // If there is an existing version live. Compare artifacts to find deleted
    // collections by looking at the number of updated collections in draft vs live.
    // If the number is different, then some draft collections were deleted
    const updatedCollections = changes.filter(a => !a.isNew).length;
    const deletedCollections = collectionsPublished - updatedCollections;

    if (deletedCollections > 0) {
      changes.push({
        name: `${deletedCollections} deleted collections`, // Can't use intl here
        lastPublished,
        lastUpdated: lastPublished,
        count: deletedCollections,
        isDelete: true,
        isNew: false,
        isDirty: true,
        type: ContentChangeType.COLLECTIONS
      });
    }

    return changes;
  }
);

export const selectToggleDialog = createSelector(
  [
    selectDraftEnvironment,
    selectIsDialogEnabledInDraftEnvironment,
    selectIsDialogEnabledInTargetEnvironment,
    selectVersions
  ],
  (
    draftEnvironment,
    isDialogEnabledInDraft,
    isDialogEnabledInLive,
    versions
  ): ContentChange[] => {
    const { latestVersion } = versions;
    const isDialogEnabled = latestVersion?.has_dialog;
    if (
      (!latestVersion && !isDialogEnabledInDraft) ||
      isDialogEnabledInDraft === isDialogEnabled
    ) {
      return [];
    }

    return [
      {
        name: `Dialog skill ${isDialogEnabledInDraft ? 'enabled' : 'disabled'}`,
        isDirty: true,
        isNew: false,
        type: ContentChangeType.DIALOG_SKILL,
        lastUpdated: draftEnvironment?.updated,
        lastPublished: draftEnvironment?.updated
      }
    ];
  }
);

/**
 *  Dear maintainer:
 *
 *  Once you are done trying to 'optimize' this selector,
 *  and have realized what a terrible mistake that was,
 *  please increment the following counter as a warning
 *  to the next guy:
 *
 *  total_hours_wasted_here = 5
 **/
export const selectAllChanges = (state: RootState): ContentChange[] => {
  const actions = selectActionChanges(state);
  const settingChanges = selectActionSkillSettingsChanges(state);
  const variables = selectVariableChanges(state);
  const collections = selectCollectionChanges(state);

  let dialogSettingChanges: ContentChange[] = [];
  let intents: ContentChange[] = [];
  let entities: ContentChange[] = [];
  let dialogs: ContentChange[] = [];

  const toggleDialogChange = selectToggleDialog(state);
  const isDialogEnabledInDraft = selectIsDialogEnabledInDraftEnvironment(state);
  if (isDialogEnabledInDraft) {
    dialogSettingChanges = selectDialogSkillSettingsChanges(state);
    intents = selectIntentChanges(state);
    entities = selectEntitiesChanges(state);
    dialogs = selectDialogChanges(state);
  }

  return [
    ...actions,
    ...variables,
    ...settingChanges,
    ...collections,
    ...intents,
    ...entities,
    ...dialogs,
    ...dialogSettingChanges,
    ...toggleDialogChange
  ];
};

export const selectIsPublishing = createSelector(
  [(state: RootState) => state.publish?.publishStatus],
  publishStatus => publishStatus === ApiStatus.LOADING
);

export const selectIsReverting = createSelector(
  [(state: RootState) => state.publish?.revertStatus],
  revertStatus => revertStatus === ApiStatus.LOADING
);

/**
 * Returns true if any part of the state that is required to calculate the
 * changes is not loaded yet.
 */
export const selectIsPublishStatusIdle = createSelector(
  [
    (state: RootState) => state.assistants.listFetched,
    (state: RootState) => state.skills.listFetched,
    (state: RootState) => state.environments.listFetched,
    (state: RootState) => state.actions.listFetched,
    (state: RootState) => state.variables.listFetched,
    (state: RootState) => state.releases.listFetched,
    (state: RootState) => state.publish.syncChangesFetched
  ],
  (
    listAssistantFetched,
    listSkillFetched,
    listEnvironmentFetched,
    listActionsFetched,
    listVariablesFetched,
    listReleasesFetched,
    syncChangesFetched
  ): boolean =>
    Boolean(
      listAssistantFetched === null ||
        listSkillFetched === null ||
        listEnvironmentFetched === null ||
        listActionsFetched === null ||
        listVariablesFetched === null ||
        listReleasesFetched === null ||
        syncChangesFetched === null
    )
);

export const selectIsPublishStatusLoading = createSelector(
  [
    (state: RootState) => state.assistants.listStatus,
    (state: RootState) => state.skills.listStatus,
    (state: RootState) => state.environments.listStatus,
    (state: RootState) => state.publish.revertStatus,
    (state: RootState) => state.actions.loadingActions,
    (state: RootState) => state.variables.loadingVariables,
    selectAreSkillSnapshotsLoading,
    areIntentsLoading,
    selectAreEntitiesLoading
  ],
  (
    listAssistantStatus,
    listSkillStatus,
    listEnvironmentStatus,
    revertStatus,
    listActions,
    listVariables,
    isSkillSnapshotLoading,
    areDialogIntentsLoading,
    areDialogEntitiesLoading
  ): boolean =>
    Boolean(
      listAssistantStatus === ApiStatus.LOADING ||
        listSkillStatus === ApiStatus.LOADING ||
        listEnvironmentStatus === ApiStatus.LOADING ||
        revertStatus === ApiStatus.LOADING ||
        listActions ||
        listVariables ||
        isSkillSnapshotLoading ||
        areDialogIntentsLoading ||
        areDialogEntitiesLoading
    )
);

/**
 * Returns true if the content (action or action and dialog) or assistant are read only.
 */
export const selectIsDraftContentReadOnly = (state: RootState): boolean => {
  const assistantId = getAssistantId(state);
  const assistantReadOnly = isAssistantReadOnly(state, assistantId);
  // note next selector incorporates isCoexistenceEnabled check
  const isDraftDialogEnabled = selectIsDialogEnabledInDraftEnvironment(state);
  const dialogSkillId = selectDialogSkillId(state);
  const actionSkillId = selectActionSkillId(state);

  const actionReadOnly = isSkillReadOnly(state, actionSkillId);
  const dialogReadOnly = isSkillReadOnly(state, dialogSkillId);

  const readOnly = actionReadOnly || assistantReadOnly;
  return readOnly || (isDraftDialogEnabled && dialogReadOnly);
};

export const selectIsPublishLimitReached = createSelector(
  [
    selectLimits,
    selectActionSnapshots,
    selectDialogSnapshots,
    selectIsDialogEnabledInDraftEnvironment
  ],
  (
    limits,
    actionSnapshots,
    dialogSnapshots,
    isDialogEnabledInDraftEnvironment
  ): boolean => {
    const snapshotLimit = limits?.snapshots || 0;

    const actionCount = actionSnapshots?.filter(
      s => s.snapshot !== 'draft'
    )?.length;
    const dialogCount = dialogSnapshots?.filter(
      s => s.snapshot !== 'draft'
    )?.length;

    return isDialogEnabledInDraftEnvironment
      ? actionCount >= snapshotLimit || dialogCount >= snapshotLimit
      : actionCount >= snapshotLimit;
  }
);

export const selectShouldSyncChanges = createSelector(
  [
    selectIsPublishing,
    selectIsReverting,
    (state: RootState) => state.publish?.syncChangesStatus === ApiStatus.LOADING
  ],
  (isPublishing, isReverting, isSync) =>
    !isPublishing && !isReverting && !isSync
);
